<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Size & Style</h3>
        <button class="btn-close-modal" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="preview-container">
          <img 
            :src="imagePath" 
            :style="previewStyle"
            :class="selectedStyle" 
            alt="Preview"
            @load="onImageLoad"
            ref="previewImage"
          />
        </div>

        <div class="size-controls-section">
          <h4>Size</h4>
          <div class="size-controls">
            <div class="size-input-group">
              <label>Width:</label>
              <input type="number" v-model.number="localWidth" placeholder="Auto" min="1" @input="onWidthChange" />
              <span>px</span>
            </div>

            <button type="button" class="btn-lock" @click="toggleLock" :title="isLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'">
              <i :class="isLocked ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
            </button>

            <div class="size-input-group">
              <label>Height:</label>
              <input type="number" v-model.number="localHeight" placeholder="Auto" min="1" @input="onHeightChange" />
              <span>px</span>
            </div>
          </div>

          <div class="size-presets">
            <button v-for="preset in presets" :key="preset.label" @click="applyPreset(preset)" class="btn-preset">{{ preset.label }}</button>
            <button @click="resetToOriginal" class="btn-preset">Original</button>
          </div>

          <p class="size-info">Original: {{ originalWidth }} × {{ originalHeight }}px</p>
        </div>

        <div class="style-controls-section">
          <h4>Image Style</h4>
          <div class="style-buttons">
            <button 
              class="btn-style" 
              :class="{ active: selectedStyle === '' }" 
              @click="selectedStyle = ''"
            >
              None
            </button>
            <button 
              class="btn-style" 
              :class="{ active: selectedStyle === 'img-round' }" 
              @click="selectedStyle = 'img-round'"
            >
              <i class="far fa-circle"></i> Round
            </button>
            <button 
              class="btn-style" 
              :class="{ active: selectedStyle === 'img-tilt-left' }" 
              @click="selectedStyle = 'img-tilt-left'"
            >
              <i class="fas fa-undo"></i> Tilt Left
            </button>
            <button 
              class="btn-style" 
              :class="{ active: selectedStyle === 'img-tilt-right' }" 
              @click="selectedStyle = 'img-tilt-right'"
            >
              <i class="fas fa-redo"></i> Tilt Right
            </button>
          </div>
        </div>

      </div>

      <div class="modal-footer">
        <button class="btn btn-cancel" @click="close">Cancel</button>
        <button class="btn btn-copy" @click="copyRichImage">
          <i class="fas fa-copy"></i> Copy Image for D2L
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageSizeModal',
  props: {
    visible: { type: Boolean, required: true },
    character: { type: Object, required: true },
  },
  data() {
    return {
      localWidth: null, localHeight: null, originalWidth: null, originalHeight: null,
      isLocked: true, aspectRatio: null,
      selectedStyle: '', // Stores the active class name
      presets: [{ label: 'Small', width: 200 }, { label: 'Medium', width: 400 }, { label: 'Large', width: 600 }],
    };
  },
  computed: {
    imagePath() { 
      let path = this.character?.path || ''; 
      if (path.startsWith('/shared/online/')) return 'https://lms.unb.ca' + path;
      return path;
    },
    previewStyle() {
      const styles = {};
      if (this.localWidth) styles.width = `${this.localWidth}px`;
      if (this.localHeight) styles.height = `${this.localHeight}px`;
      styles.maxWidth = '100%';
      styles.maxHeight = '400px';
      return styles;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.localWidth = this.character.preferredWidth || null;
        this.localHeight = this.character.preferredHeight || null;
        this.selectedStyle = ''; // Reset style when opening
      }
    },
  },
  methods: {
    onImageLoad(event) {
      const img = event.target;
      this.originalWidth = img.naturalWidth;
      this.originalHeight = img.naturalHeight;
      this.aspectRatio = img.naturalWidth / img.naturalHeight;
      if (!this.localWidth && !this.localHeight && this.character.preferredWidth) {
        this.localWidth = this.character.preferredWidth;
        if (this.isLocked && this.aspectRatio) this.localHeight = Math.round(this.localWidth / this.aspectRatio);
      }
    },
    toggleLock() { this.isLocked = !this.isLocked; },
    onWidthChange() { if (this.isLocked && this.aspectRatio && this.localWidth) this.localHeight = Math.round(this.localWidth / this.aspectRatio); },
    onHeightChange() { if (this.isLocked && this.aspectRatio && this.localHeight) this.localWidth = Math.round(this.localHeight * this.aspectRatio); },
    applyPreset(preset) { this.localWidth = preset.width; if (this.isLocked && this.aspectRatio) this.localHeight = Math.round(preset.width / this.aspectRatio); },
    resetToOriginal() { this.localWidth = null; this.localHeight = null; },
    
    generateHtml() {
      // Use resolved path for the actual copy
      let imagePath = this.character.path.trim();
      if (imagePath.startsWith('/shared/online/')) imagePath = 'https://lms.unb.ca' + imagePath;
      
      const title = this.character.title || this.character.name || 'Image';
      
      let styleAttr = 'max-width: 100%;';
      if (this.localWidth || this.localHeight) {
        const w = this.localWidth ? `width: ${this.localWidth}px;` : '';
        const h = this.localHeight ? `height: ${this.localHeight}px;` : '';
        styleAttr = `${w} ${h} max-width: 100%;`.trim();
      }

      // Add Class if selected
      const classAttr = this.selectedStyle ? `class="${this.selectedStyle}"` : '';

      return `<p><img src="${imagePath}" ${classAttr} alt="${title}" title="${title}" data-d2l-editor-default-img-style="true" style="${styleAttr}"></p>`;
    },

    async copyRichImage() {
      const htmlCode = this.generateHtml();
      try {
        const blobHtml = new Blob([htmlCode], { type: "text/html" });
        const blobText = new Blob([htmlCode], { type: "text/plain" });
        await navigator.clipboard.write([new ClipboardItem({ "text/html": blobHtml, "text/plain": blobText })]);
        this.$emit('copied');
        this.close();
      } catch (err) {
        console.warn("Rich copy failed, falling back to text:", err);
        navigator.clipboard.writeText(htmlCode).then(() => {
          this.$emit('copied');
          this.close();
        });
      }
    },
    close() { this.$emit('close'); },
  },
};
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

/* CSS FOR PREVIEWING STYLES LOCALLY */
.img-round { border-radius: 50%; }
.img-tilt-left { transform: rotate(-3deg); }
.img-tilt-right { transform: rotate(3deg); }

.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background-color: white; border-radius: 8px; width: 90%; max-width: 600px; max-height: 90vh; overflow-y: auto; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3); }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 2px solid #e0e0e0; }
.modal-header h3 { margin: 0; font-size: 20px; }
.btn-close-modal { background: none; border: none; font-size: 24px; cursor: pointer; color: #666; transition: color 0.3s; }
.btn-close-modal:hover { color: #000; }
.modal-body { padding: 20px; }
.preview-container { display: flex; justify-content: center; align-items: center; min-height: 200px; background-color: #f5f5f5; border-radius: 8px; padding: 20px; margin-bottom: 20px; overflow: hidden; }
.preview-container img { display: block; object-fit: contain; transition: transform 0.3s; }

/* SECTIONS */
.size-controls-section, .style-controls-section { margin-top: 20px; padding-top: 20px; border-top: 1px solid #eee; }
h4 { margin: 0 0 10px 0; font-size: 14px; text-transform: uppercase; color: #666; }

.size-controls { display: flex; align-items: center; gap: 15px; margin-bottom: 15px; }
.size-input-group { display: flex; align-items: center; gap: 8px; flex: 1; }
.size-input-group label { font-weight: bold; min-width: 50px; }
.size-input-group input { flex: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 14px; }
.size-input-group span { font-size: 14px; color: #666; }
.btn-lock { background-color: transparent; border: 2px solid #000; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s ease; flex-shrink: 0; }
.btn-lock:hover { background-color: rgba(0, 0, 0, 0.05); }
.size-presets { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 15px; }
.btn-preset { padding: 8px 16px; background-color: #f0f0f0; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; font-size: 14px; transition: all 0.3s; }
.btn-preset:hover { background-color: #e0e0e0; border-color: #999; }
.size-info { font-size: 12px; color: #666; margin: 0; }

/* STYLE BUTTONS */
.style-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
.btn-style { flex: 1; padding: 10px; border: 2px solid #eee; background: white; border-radius: 8px; cursor: pointer; font-weight: bold; color: #555; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; }
.btn-style:hover { background: #f9f9f9; border-color: #ccc; }
.btn-style.active { border-color: var(--accent-color); background: #e0f7fa; color: var(--accent-color); }

.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 20px; border-top: 2px solid #e0e0e0; }
.btn { padding: 10px 20px; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: bold; transition: opacity 0.3s; }
.btn:hover { opacity: 0.9; }
.btn-cancel { background-color: #e0e0e0; color: #333; }
.btn-copy { background-color: var(--accent-color); color: white; display: flex; align-items: center; gap: 8px; }

@media (max-width: 768px) {
  .modal-content { width: 95%; }
  .size-controls { flex-direction: column; align-items: stretch; }
  .btn-lock { align-self: center; }
  .size-presets { justify-content: center; }
}
</style>